import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { isLoggedIn } from 'common/utils/auth';
import { isNativeApp } from 'common/webview';
import { IcPhone, IcUser, IcInfo } from 'components/common/icons/EmojiIcons';
import {
    AppScreen, Button, ButtonFooter, TextField, FieldGroup,
    ActionList, ActionRow, DividerText, PageIntro,
} from 'components/ds';
import styles from './InvitationInfo.module.css';

type JoinMode = null | 'login' | 'guest';

export const InvitationInfo = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { inviteCode: rawInviteCode } = useParams();
    const inviteCode = rawInviteCode?.toUpperCase();
    const location = useLocation();
    const { showAlert } = useAlert();
    const meeting = location.state?.meeting;

    const inApp = isNativeApp();
    // 앱 안에서 로그인 상태면 바로 로그인 모드로 시작
    const [joinMode, setJoinMode] = useState<JoinMode>(inApp && isLoggedIn() ? 'login' : null);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [appOpenFailed, setAppOpenFailed] = useState(false);

    const handlePhoneChange = (value: string) => {
        const numbers = value.replace(/[^0-9]/g, '');
        if (numbers.length <= 3) {
            setPhone(numbers);
        } else if (numbers.length <= 7) {
            setPhone(`${numbers.slice(0, 3)}-${numbers.slice(3)}`);
        } else if (numbers.length <= 11) {
            setPhone(`${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`);
        }
    };

    const isGuestFormValid = name.trim().length > 0 && phone.replace(/-/g, '').length >= 10;

    // 앱으로 로그인 참여
    const handleOpenApp = () => {
        const deepLink = `dokocrew://invitation/${inviteCode}`;

        // iframe 방식으로 딥링크 시도
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = deepLink;
        document.body.appendChild(iframe);

        // 2초 후 앱이 안 열렸으면 실패 표시
        const timer = setTimeout(() => {
            setAppOpenFailed(true);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 2000);

        // 페이지 포커스를 잃으면 앱이 열린 것
        const handleBlur = () => {
            clearTimeout(timer);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            window.removeEventListener('blur', handleBlur);
        };
        window.addEventListener('blur', handleBlur);
    };

    // 이미 로그인 상태면 초대코드로 직접 참여
    const handleLoginJoin = async () => {
        if (!inviteCode) return;

        if (isLoggedIn()) {
            setIsJoining(true);
            try {
                const response = await MeetingRepository.onPostJoinMeetingByCode(inviteCode);
                if (response?.status === 200 || response?.status === 201) {
                    // 신규 참여 또는 이미 참여 중 → meetingId 반환됨
                    const meetingId = response?.data?.data || response?.data?.meetingId;
                    // 초대 [참여] 클릭 = 참석 의사 명확 → ATTENDING 사전 설정 (모임상세 재차 prompt 회피)
                    if (meetingId) {
                        try {
                            await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: 'ATTENDING' });
                        } catch (e) {
                            console.warn('초대 참여 후 ATTENDING 사전 설정 실패', e);
                        }
                    }
                    if (inApp && meetingId) {
                        navigate(`/meeting/detail/${meetingId}`, { replace: true, state: { meetingName: meeting?.meetingName } });
                    } else {
                        navigate(`/invitation/result/${inviteCode}`, {
                            state: { meeting, meetingId, isLoggedIn: true },
                            replace: true,
                        });
                    }
                    return;
                }
            } catch { /* fall through */ }
            setIsJoining(false);
            await showAlert({ message: t('invitation.joinFailed') });
        } else {
            // 로그인 안 된 상태 → 로그인 페이지로 (로그인 후 돌아옴)
            navigate(`/login?returnUrl=${encodeURIComponent(`/invitation/info/${inviteCode}`)}`);
        }
    };

    // 비회원(게스트) 참여
    const handleGuestJoin = async () => {
        if (!isGuestFormValid || !inviteCode) return;

        setIsJoining(true);
        try {
            const response = await MeetingRepository.onPostJoinMeetingAsGuest(inviteCode, {
                nickname: name.trim(),
                phone: phone.replace(/-/g, ''),
            });

            if (response?.status === 200 || response?.status === 201) {
                const data = response?.data?.data || response?.data;
                const meetingId = data?.meetingId || data;

                // 게스트 세션 저장
                if (data?.guestToken) {
                    sessionStorage.setItem('guestToken', data.guestToken);
                    sessionStorage.setItem('guestUserNo', String(data.guestUserNo));
                    sessionStorage.setItem('guestNickname', data.nickname);
                    sessionStorage.setItem('guestMeetingId', typeof meetingId === 'string' ? meetingId : '');
                }

                navigate(`/invitation/result/${inviteCode}`, {
                    state: { meeting, meetingId: typeof meetingId === 'string' ? meetingId : undefined, isGuest: true },
                    replace: true,
                });
            } else {
                navigate(`/invitation/result/${inviteCode}`, {
                    state: { meeting, isGuest: true },
                    replace: true,
                });
            }
        } catch {
            navigate(`/invitation/result/${inviteCode}`, {
                state: { meeting, isGuest: true },
                replace: true,
            });
        } finally {
            setIsJoining(false);
        }
    };

    // 모드 선택 화면
    if (joinMode === null) {
        return (
            <AppScreen title={t('invitation.selectMethod')} bar="sub">
                <PageIntro
                    title={t('invitation.chooseMethod')}
                    desc={t('invitation.chooseMethodDesc')}
                />

                <ActionList>
                    <ActionRow
                        icon={<IcPhone size={22} />}
                        title={t('invitation.loginJoin')}
                        desc={t('invitation.loginJoinDesc')}
                        onClick={() => setJoinMode('login')}
                    />
                    <ActionRow
                        icon={<IcUser size={22} />}
                        title={t('invitation.guestJoin')}
                        desc={t('invitation.guestJoinDesc')}
                        onClick={() => setJoinMode('guest')}
                    />
                </ActionList>

                {/* 이미 비회원으로 참여한 경우 */}
                {meeting?.meetingId && (
                    <div className={styles.switchWrap}>
                        <Button
                            variant="ghost"
                            onClick={() => navigate(`/guest-login/${meeting.meetingId}`)}
                        >
                            {t('invitation.guestAlreadyJoined')}
                        </Button>
                    </div>
                )}
            </AppScreen>
        );
    }

    // 회원 로그인 참여
    if (joinMode === 'login') {
        // 앱 안에서 로그인 상태 → 바로 참여 버튼만 표시
        if (inApp && isLoggedIn()) {
            return (
                <AppScreen title={t('invitation.meetingJoin')} bar="sub">
                    <PageIntro
                        title={<>{meeting?.meetingName || t('meeting.detail')}{t('invitation.joinConfirm')}</>}
                        desc={meeting?.description || undefined}
                    />
                    <div className={styles.section}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleLoginJoin}
                            disabled={isJoining}
                        >
                            {isJoining ? t('invitation.joining') : t('invitation.join')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="lg"
                            onClick={() => navigate('/', { replace: true })}
                        >
                            {t('common.cancel')}
                        </Button>
                    </div>
                </AppScreen>
            );
        }

        // 브라우저: 앱 열기 / 웹 로그인 / 비회원 전환
        return (
            <AppScreen title={t('invitation.memberJoin')} bar="sub">
                <PageIntro
                    title={t('invitation.joinInApp')}
                    desc={t('invitation.joinInAppDesc')}
                />

                <div className={styles.section}>
                    <Button
                        variant="primary"
                        size="lg"
                        icon={<IcPhone size={18} />}
                        onClick={handleOpenApp}
                    >
                        {t('invitation.openApp')}
                    </Button>

                    {appOpenFailed && (
                        <div className={styles.failNotice}>
                            <span className={styles.failIcon}><IcInfo size={18} /></span>
                            <div className={styles.failBody}>
                                <p>{t('invitation.cannotOpenApp')}</p>
                                <p>{t('invitation.appNotInstalled')}</p>
                                <div className={styles.storeButtons}>
                                    <a
                                        className={styles.storeBtn}
                                        href="https://apps.apple.com/app/dokocrew/id000000000"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        App Store
                                    </a>
                                    <a
                                        className={styles.storeBtn}
                                        href="https://play.google.com/store/apps/details?id=com.dokoiot.dokocrew"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Play Store
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}

                    <DividerText label={t('invitation.or')} />

                    <Button
                        variant="secondary"
                        size="lg"
                        onClick={handleLoginJoin}
                        disabled={isJoining}
                    >
                        {isJoining ? t('invitation.joining') : t('invitation.webLogin')}
                    </Button>

                    <Button
                        variant="ghost"
                        size="lg"
                        onClick={() => { setJoinMode('guest'); setAppOpenFailed(false); }}
                    >
                        {t('invitation.guestSwitch')}
                    </Button>
                </div>
            </AppScreen>
        );
    }

    // 비회원 참여
    return (
        <AppScreen title={t('invitation.guestJoinTitle')} bar="sub" hasFooter>
            <PageIntro
                title={t('invitation.guestInfoTitle')}
                desc={t('invitation.guestInfoDesc')}
            />

            <FieldGroup>
                <TextField
                    id="name"
                    label={t('invitation.nameLabel')}
                    required
                    type="text"
                    placeholder={t('invitation.namePlaceholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={20}
                />
                <TextField
                    id="phone"
                    label={t('invitation.phoneLabel')}
                    required
                    type="tel"
                    inputMode="numeric"
                    numeric
                    placeholder={t('invitation.phonePlaceholder')}
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                />
            </FieldGroup>

            <div className={styles.switchWrap}>
                <Button variant="ghost" onClick={() => setJoinMode('login')}>
                    {t('invitation.loginSwitch')}
                </Button>
            </div>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleGuestJoin}
                    disabled={!isGuestFormValid || isJoining}
                >
                    {isJoining ? t('invitation.joining') : t('invitation.join')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
